import React from 'react';
import { BrowserRouter as Router, Route, Redirect, Switch } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createBrowserHistory } from 'history';
import axios from 'axios';

import { ToastContainer, toast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import { FaTimes } from 'react-icons/fa';
import './App.scss';

// Store.
import { RootState } from './store/';
import {
  probeAuthSession,
  refreshUserProfile,
} from './store/user/actions';
import {
  AUTH_SESSION_PROBE_MS,
  BUILD_VERSION_CHECK_MS,
  USER_PROFILE_REFRESH_MS,
} from './config/backgroundSyncConfig';
import { recordBackgroundPoll } from './utils/backgroundSyncMetrics';
import { removeAlertAction } from './store/alert/actions';

// Components.
import OrderTypeChooser from './components/b2b/OrderTypeChooser';
import PreOrderChooser from './components/b2b/PreOrderChooser';
import ProductListContainer from './components/b2b/ProductListContainer';
import WishlistProductListContainer from './components/b2b/WishlistProductListContainer';
import EditNavOrderContainer from './components/b2b/EditNavOrderContainer';
import CollectionListContainer from './components/b2b/CollectionListContainer';
import MainContainer from './components/layout/MainContainer';
import DashboardContainer from './components/layout/DashboardContainer';
import CheckoutContainer from './components/b2b/CheckoutContainer';
import Dashboard from './components/shared/Dashboard/Dashboard';
import SearchPage from './components/shared/SearchPage';
import Login from './components/auth/Login';
import Logout from './components/auth/Logout';
import ForgotPassword from './components/auth/ForgotPassword';
import MyMolo from './components/b2b/MyMolo';
import StockHasChangedModal from './components/b2b/StockHasChangedOverlay';
import OrderChangedBanner from './components/b2b/OrderChangedBanner';
import WishlistSync from './components/b2b/WishlistSync';
import OrderRevisionConflictModal from './components/b2b/OrderRevisionConflictModal';
import OrderActivityMessenger from './components/b2b/OrderActivityMessenger';
import LookUpNavOrder from './components/b2b/LookUpNavOrder';
import ProductDownloadImagesPage from './components/b2b/ProductDownloadImagesPage';
import OrderHistoryPage from './components/b2b/OrderHistoryPage';
import MyOrdersPage from './components/b2b/MyOrdersPage';
import EditOrderHandlerOverlay from './components/b2b/EditOrderHandlerOverlay';
import ConfirmModal from './components/layout/Modal/ConfirmModal';

export const isAuthenticated = (user: any) => {
  // If there already is a user.
  if (user && user.user) {
    return true;
  }

  // Fallback if all tries fails.
  return false;
}

const PrivateRoute = ({ containerType, routeComponent, isAuthenticated, desktopOnly, ...rest }: any) => {
  const RouteComponent = routeComponent;

  const renderShopContainer = (props: any) => (
    <MainContainer {...props}>
      <RouteComponent {...props} />
    </MainContainer>
  )

  if (!isAuthenticated) {
    return <Redirect to={{ pathname: '/login', state: { referer: rest.location } }} />
  }

  if (containerType === 'dashboard') {
    return (
      <Route
        {...rest}
        render={props => {
          return (
            <DashboardContainer key={props.match.params.orderId || 'empty'}>
              <RouteComponent {...props} />
            </DashboardContainer>
          )
        }}
      />
    )
  }

  if (containerType === 'checkout') {
    return (
      <Route
        {...rest}
        render={props => {
          return (
            <DashboardContainer hideBasket={true}>
              <RouteComponent {...props} />
            </DashboardContainer>
          )
        }}
      />
    )
  }

  return (
    <Route
      {...rest}
      render={props => {
        if (desktopOnly) {
          return (
            <React.Fragment>
              <div className="desktop-only">
                {renderShopContainer(props)}
              </div>
              <div className="mobile-only">
                  <div className="loading-spinner-1">
                    <h2>Desktop only!</h2>
                    <p style={{marginBottom: '70px', textAlign: 'center'}}>A mobile friendly version will be available soon :-)</p>
                  </div>
              </div>
            </React.Fragment>
          )
        }

        return (
          renderShopContainer(props)
        )
      }}
    />)
};

const App: React.FC = () => {

  const user = useSelector((state: RootState) => state.user.user);
  const alerts = useSelector((state: RootState) => state.alertStore.alertState);
  const dispatch = useDispatch();
  const appUserHasBeenValidated = useSelector((state: RootState) => state.user.user.isValidated);
  const [showCriticalModal, setShowCriticalModal] = React.useState(false);
  const [criticalMessage, setCriticalMessage] = React.useState('');
  // Create the shared history
  const history = createBrowserHistory();

  history.listen((location, action) => {
    // clear alert on location change
    toast.dismiss()
    // dispatch(clearActions());
  });

  React.useEffect(() => {
    if (!alerts.length) {
      return;
    }

    const alert = alerts[0];

    switch (alert.type) {
      case 'success':
        toast.success(alert.message);
        break;
      case 'error':
        toast.error(alert.message);
        break;
      case 'critical':
        setCriticalMessage(alert.message);
        setShowCriticalModal(true);
        break;
    }

    dispatch(removeAlertAction(alert.id));
  }, [alerts, dispatch]);

  React.useEffect(() => {
    if (!appUserHasBeenValidated) {
      dispatch(refreshUserProfile());
    }
  }, [appUserHasBeenValidated, dispatch]);

  React.useEffect(() => {
    let sessionTimer: any;
    let profileTimer: any;
    let timerForBuild: any;
    checkVersion();

    const pollAuthSession = () => {
      if (!document.hidden && appUserHasBeenValidated) {
        dispatch(probeAuthSession());
      }
    };

    const pollUserProfile = () => {
      if (!document.hidden && appUserHasBeenValidated) {
        dispatch(refreshUserProfile());
      }
    };

    sessionTimer = setInterval(pollAuthSession, AUTH_SESSION_PROBE_MS);
    profileTimer = setInterval(pollUserProfile, USER_PROFILE_REFRESH_MS);
    timerForBuild = setInterval(() => {
      if (!document.hidden) {
        checkVersion();
      }
    }, BUILD_VERSION_CHECK_MS);

    const onVisibilityChange = () => {
      if (document.hidden || !appUserHasBeenValidated) {
        return;
      }
      dispatch(probeAuthSession());
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(sessionTimer);
      clearInterval(profileTimer);
      clearInterval(timerForBuild);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [appUserHasBeenValidated, dispatch]);

  const checkVersion =  async () => {
    // if (process.env.NODE_ENV === 'production') {
      const buildResponse = await axios.get('/build.json?' + (new Date().getTime()/1000));
      recordBackgroundPoll('build.json');
      const localStorageBuild = localStorage.getItem('build');
      const buildVersion = buildResponse.data.buildVersion;

      if ((localStorageBuild && localStorageBuild !== buildVersion) || !localStorageBuild) {
        console.log('Refresh application...')
        window.location.reload();
      }

      localStorage.setItem('build', buildVersion);
    // }
  }

  const CriticalModal = () => {
    return <ConfirmModal
      isShowing={showCriticalModal}
      hide={() => { setShowCriticalModal(false) }}
      content={(
        <div className="delete-order-modal">
            <h2>Something when wrong!</h2>
            <p>{ criticalMessage }</p>
            <div className="delete-order-modal__footer">
                <button className="btn" onClick={() => { setShowCriticalModal(false) }}>Close</button>
            </div>
        </div>
      )}
    />
  }

  if (!appUserHasBeenValidated) {
    return <div style={{ height: '100vh', width: '100vw' }}></div>
  }

  return (
    <div className="App">
      {/* <div className="desktop-only"> */}
        <ToastContainer
          position={toast.POSITION.BOTTOM_LEFT}
          autoClose={2000}
          closeButton={<FaTimes />}
        />
        
        <Router>
          <Route exact path="/login" component={Login} />
          {/* <Route exact path="/logout" component={Logout} /> */}
          <Route exact path="/forgot-password" component={ForgotPassword} />
          {isAuthenticated(user) && (
            <>
              <StockHasChangedModal />
              <OrderChangedBanner />
              <WishlistSync />
              <OrderRevisionConflictModal />
              <OrderActivityMessenger />
              <EditOrderHandlerOverlay />
            </>
          )}

          <Switch>
            <PrivateRoute exact path="/logout" isAuthenticated={isAuthenticated(user)} routeComponent={Logout} />
            <PrivateRoute exact path="/previeworder/:appOrderId" isAuthenticated={isAuthenticated(user)} routeComponent={LookUpNavOrder} desktopOnly={false} />
            <PrivateRoute exact path="/search/:query?" isAuthenticated={isAuthenticated(user)} routeComponent={SearchPage}  desktopOnly={true} />
            <PrivateRoute exact path="/dashboard" isAuthenticated={isAuthenticated(user)} routeComponent={Dashboard} containerType={'dashboard'}  desktopOnly={false} />  
            <PrivateRoute exact path="/dashboard/:orderId" isAuthenticated={isAuthenticated(user)}  containerType={'dashboard'} routeComponent={Dashboard} desktopOnly={false} />
            <PrivateRoute exact path="/checkout/:orderId" isAuthenticated={isAuthenticated(user)} routeComponent={CheckoutContainer} containerType='checkout' desktopOnly={true}/>
            <PrivateRoute exact path="/" isAuthenticated={isAuthenticated(user)} routeComponent={OrderTypeChooser} desktopOnly={true} />
            <PrivateRoute exact path="/shop" isAuthenticated={isAuthenticated(user)} routeComponent={OrderTypeChooser} desktopOnly={true} />
            <PrivateRoute exact path="/shop/wishlist/:listname" isAuthenticated={isAuthenticated(user)} routeComponent={WishlistProductListContainer} desktopOnly={true} />
            <PrivateRoute exact path="/shop/:orderType" isAuthenticated={isAuthenticated(user)} routeComponent={CollectionListContainer} desktopOnly={true} />
            <PrivateRoute exact path="/preOrderChooser" isAuthenticated={isAuthenticated(user)} routeComponent={PreOrderChooser} desktopOnly={true} />
            <PrivateRoute exact path="/shop/:orderType/:collection" isAuthenticated={isAuthenticated(user)} routeComponent={ProductListContainer} desktopOnly={true} />
            <PrivateRoute exact path="/edit/:collection?" isAuthenticated={isAuthenticated(user)} routeComponent={EditNavOrderContainer} desktopOnly={true} />
            <PrivateRoute exact path="/my-molo" isAuthenticated={isAuthenticated(user)} routeComponent={MyMolo} desktopOnly={true} />
            <PrivateRoute exact path="/activity-history" isAuthenticated={isAuthenticated(user)} routeComponent={OrderHistoryPage} desktopOnly={true} />
            <Route exact path="/order-history">
              <Redirect to="/activity-history" />
            </Route>
            <PrivateRoute exact path="/my-orders" isAuthenticated={isAuthenticated(user)} routeComponent={MyOrdersPage} desktopOnly={true} />
            <PrivateRoute exact path="/download" isAuthenticated={isAuthenticated(user)} routeComponent={ProductDownloadImagesPage} desktopOnly={true} />
          </Switch>
        </Router>
        <CriticalModal />
      {/* </div> */}
      {/* <div className="mobile-only">
        
          <div className="loading-spinner-1">
            <h2>Desktop only!</h2>
            <p style={{marginBottom: '70px'}}>A mobile friendly version will be available soon :-)</p>
          </div>
      </div> */}
    </div>
  );
}

export default App;
